import React, {useState, useEffect, useMemo, useRef} from "react";
import { useLocation , useNavigate } from "react-router";
import { readContract } from "@wagmi/core";
import { createClient, configureChains, mainnet } from '@wagmi/core'
import { publicProvider } from '@wagmi/core/providers/public'
import ArtblocksJSON from "../lib/contracts/artblocks-contract.json";
import ArtblocksJSONV0 from "../lib/contracts/artblocks-contract-v0.json";
import { libraries, defaultCode } from "../lib/utils";
import Button from "../components/base/Button";
import {BiSave} from "react-icons/bi";
import {MdArrowBack} from "react-icons/md";
import "../styles/base/ProjectDetail.css"
import * as htmlToImage from 'html-to-image';
import html2canvas from 'html2canvas';
import { CONTRACT_ADDRESSES } from "../constants/Contracts";
import { execute } from "../.graphclient";
import { gql } from '@graphql-mesh/utils';

// Create a GraphQL document for querying token hash
const ArtblocksTokenDocument = gql`
  query ArtblocksToken($id: ID!) {
    token(id: $id) {
      id
      tokenId
      hash
      project {
        id
        projectId
      }
    }
  }
`;

const ProjectDetail = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [library, setLibrary] = useState("p5");
  const [code, setCode] = useState(defaultCode);
  const checkCanvas = useRef(null);

  const libraryScript = useMemo(() => {
    const lib = libraries[library] || libraries["p5"];
    return `<script src="${lib}"></script>`;
  }, [library]);

  const { provider, webSocketProvider } = configureChains(
    [mainnet],
    [publicProvider()],
  )
   
  const client = createClient({
    provider,
    webSocketProvider,
  })

  const SaveImagefunction = () => {
    if(library != "three"){
      const canvasTarget = document.getElementById("liveArtBlock").contentWindow.document.body;
      htmlToImage.toPng(canvasTarget)
      .then(function (dataUrl) {
        var link = document.createElement('a');
        link.download = `ArtBlock${state?.collectionInfo?.projectId * 1000000 + state?.projectIndex || 0}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch(function (error) {
        console.error('oops, something went wrong!', error);
      });
    } else {
      console.log("this is three js image")
    }
  }

  useEffect(()=>{
    // Guard checks
    if (!state || !state.collectionInfo) {
      console.error("Missing state or collectionInfo");
      return;
    }

    function removeSaveImagefunction() {
      console.log("======================")
      const iframeEle = document.getElementById("liveArtBlock");
      if (!iframeEle) return;
      
      checkCanvas.current = setInterval(() => {
        console.log("----------------------")
        try {
          let canvasEles = iframeEle.contentWindow.document.getElementsByTagName("canvas");
          if (canvasEles.length > 0) {
            for (let i = 0; i < canvasEles.length; i++) {
              canvasEles[i].addEventListener("contextmenu", function(e) {
                e.preventDefault();
              });
            }
            clearInterval(checkCanvas.current);
          }
        } catch (e) {
          // Iframe might not be ready yet
        }
      }, 1000);
    }

    // Get script and scriptType directly from state
    let scriptType = state.collectionInfo.scriptType || "p5";
    if (scriptType === "js" || scriptType === "javascript") {
      scriptType = "p5";
    }
    scriptType = scriptType.replace("p5js", "p5").replace("threejs", "three");
    
    const script = state.collectionInfo.script || defaultCode;
    
    // Update React state
    setLibrary(scriptType);
    setCode(script);

    console.log("Setting up project:", {
      projectId: state.collectionInfo.projectId,
      projectIndex: state.projectIndex,
      contractAddress: state.collectionInfo.contractAddress,
      scriptType: scriptType,
      hasScript: !!script
    });

    const tokenId = state.collectionInfo.projectId * 1000000 + state.projectIndex;
    console.log("Reading token for tokenId:", tokenId);

    // Try to get hash from GraphQL first, then contract, then placeholder
    (async () => {
      let hash = null;
      
      // Step 1: Try GraphQL subgraph (most reliable for minted tokens)
      try {
        const tokenGraphQLId = `${state.collectionInfo.contractAddress.toLowerCase()}-${tokenId}`;
        console.log("Querying GraphQL for token:", tokenGraphQLId);
        
        const result = await execute(ArtblocksTokenDocument, { id: tokenGraphQLId });
        
        if (result?.data?.token?.hash) {
          hash = result.data.token.hash;
          console.log("Got hash from GraphQL:", hash);
        } else {
          console.log("Token not found in GraphQL (may not be minted yet)");
        }
      } catch (error) {
        console.log("GraphQL query failed, trying contract call:", error);
      }
      
      // Step 2: If GraphQL didn't work, try contract call
      if (!hash) {
        try {
          const functionName =
            state.collectionInfo.contractAddress?.toLowerCase() === CONTRACT_ADDRESSES.ARTBLOCKS_V0.toLowerCase()
            ? "showTokenHashes"
            : "tokenIdToHash";
          
          const abi =
            state.collectionInfo.contractAddress?.toLowerCase() === CONTRACT_ADDRESSES.ARTBLOCKS_V0.toLowerCase()
            ? ArtblocksJSONV0.abi
            : ArtblocksJSON.abi;
          
          hash = await readContract({
            chainId: 1,
            // @ts-ignore
            address: state.collectionInfo.contractAddress,
            abi: abi,
            functionName: functionName,
            // @ts-ignore
            args: [tokenId],
          });
          
          console.log("Got hash from contract:", hash);
        } catch (error) {
          console.log("Contract call failed, using placeholder hash");
        }
      }
      
      // Step 3: If both failed, generate placeholder hash
      if (!hash) {
        const seed = tokenId.toString();
        let hashHex = "0x";
        for (let i = 0; i < 64; i++) {
          const charIndex = (parseInt(seed || "0") + i * 13 + (i % 3) * 7) % 16;
          hashHex += charIndex.toString(16);
        }
        hash = hashHex;
        console.log("Generated placeholder hash:", hash);
      }
      
      // Render with the hash we got (real or placeholder)
      renderVisualization(hash, script, scriptType);
    })();

    function renderVisualization(hash, scriptToUse, scriptTypeToUse) {
      const tokenData = `window.tokenData={"tokenId": "${state.projectIndex}", "hash": "${hash}", "hashes": ["${hash}"], "minter": "Owner"};`;
      
      // Get the library script, defaulting to p5 if scriptType not found
      const lib = libraries[scriptTypeToUse] || libraries["p5"];
      const libScript = `<script src="${lib}"></script>`;
      
      // @ts-ignore
      const wrappedCode = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
${libScript}
<script>
  // Suppress WebGL errors in iframe context
  const originalError = console.error;
  console.error = function(...args) {
    const errorStr = args.join(' ');
    if (errorStr.includes('WebGL') || errorStr.includes('shader') || errorStr.includes('useProgram') || errorStr.includes('CALL_EXCEPTION')) {
      return;
    }
    originalError.apply(console, args);
  };
</script>
</head>
<body style="margin: 0px; padding: 0px; width: 100vw; height: 100vh; overflow: hidden;">
<script>
${tokenData}
${scriptToUse}
</script>
</body>
</html>`;
      
      const iframe = document.getElementById("liveArtBlock");
      if (iframe) {
        console.log("Setting iframe srcdoc with hash:", hash.substring(0, 20) + "...");
        // Always set srcdoc, even if script is defaultCode
        iframe.srcdoc = wrappedCode;
        
        // Wait a bit before setting up canvas listeners
        setTimeout(() => {
          removeSaveImagefunction();
        }, 500);
      } else {
        console.error("Iframe not found!");
      }
    }
    
    return ()=>{
      console.log("Clean up function called")
      if (checkCanvas.current) {
        clearInterval(checkCanvas.current);
      }
    }
  },[state, libraryScript])

  return (
    <div style={{width : `${window.innerWidth}px`, height : `${window.innerHeight}px`, overflow : "hidden"}}>
      <div className="projectDetailController">
        <Button
          width="50px"
          height="50px"
          child={
              <div id="button-child">
              <MdArrowBack size="28px" />
              </div>
          }
          onClick = {()=>{navigate(-1)}}
        ></Button>
        <Button
          width="50px"
          height="50px"
          child={
              <div id="button-child">
              <BiSave size="28px" />
              </div>
          }
          onClick = {()=>{SaveImagefunction()}}
        ></Button>
      </div>
      <iframe
        scrolling="no"
        style={{ overflow: "hidden", width : '100%', height : '100%'}}
        className="overflow-hidden w-full h-full col-span-3"
        id="liveArtBlock"
        title="liveArtBlock"
      />
    </div>
  );
};
export default ProjectDetail;
